import { useState, useEffect, useCallback, useRef } from "react";
import { useAdminAuth } from "@/lib/adminAuth";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

interface OrderAgent {
  name: string;
  price: number;
}

interface Order {
  id: number;
  createdAt: string;
  customerName: string;
  email: string;
  company: string | null;
  agents: OrderAgent[];
  totalPrice: number;
  depositAmount: number;
  status: string;
  stripePaymentIntentId: string | null;
  couponCode: string | null;
  discountAmount: number | null;
}

interface Filters {
  from: string;
  to: string;
  customer: string;
  company: string;
  agents: string[];
}

const EMPTY_FILTERS: Filters = {
  from: "",
  to: "",
  customer: "",
  company: "",
  agents: [],
};

function countActiveFilters(f: Filters): number {
  return (
    (f.from ? 1 : 0) +
    (f.to ? 1 : 0) +
    (f.customer ? 1 : 0) +
    (f.company ? 1 : 0) +
    (f.agents.length > 0 ? 1 : 0)
  );
}

function buildQuery(f: Filters): string {
  const p = new URLSearchParams();
  if (f.from) p.set("from", f.from);
  if (f.to) p.set("to", f.to);
  if (f.customer) p.set("customer", f.customer);
  if (f.company) p.set("company", f.company);
  if (f.agents.length > 0) p.set("agents", f.agents.join(","));
  const qs = p.toString();
  return qs ? `api/admin/orders?${qs}` : "api/admin/orders";
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(n: number): string {
  return `$${n.toLocaleString()}`;
}

interface AgentMultiSelectProps {
  allAgents: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}

function AgentMultiSelect({ allAgents, selected, onChange }: AgentMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const toggle = (name: string) => {
    if (selected.includes(name)) {
      onChange(selected.filter((n) => n !== name));
    } else {
      onChange([...selected, name]);
    }
  };

  const label =
    selected.length === 0
      ? "Any agent"
      : selected.length === 1
      ? selected[0]
      : `${selected.length} agents`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 hover:border-white/20 hover:bg-white/10 transition min-w-[160px] justify-between"
      >
        <span className="truncate max-w-[120px]">{label}</span>
        <svg
          className={`w-3.5 h-3.5 text-white/40 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-56 max-h-64 overflow-y-auto rounded-lg border border-white/10 bg-[#0f1117] shadow-2xl">
          {allAgents.length === 0 && (
            <div className="px-3 py-2 text-xs text-white/30">No agents</div>
          )}
          {allAgents.map((name) => (
            <label
              key={name}
              className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-white/5 transition"
            >
              <input
                type="checkbox"
                checked={selected.includes(name)}
                onChange={() => toggle(name)}
                className="rounded border-white/20 bg-white/5 text-cyan-500 focus:ring-0 focus:ring-offset-0 w-3.5 h-3.5"
              />
              <span className="text-xs text-white/80 truncate">{name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

interface FilterBarProps {
  filters: Filters;
  allAgents: string[];
  activeCount: number;
  onChange: (f: Filters) => void;
  onClear: () => void;
}

function FilterBar({ filters, allAgents, activeCount, onChange, onClear }: FilterBarProps) {
  const set = <K extends keyof Filters>(key: K, val: Filters[K]) =>
    onChange({ ...filters, [key]: val });

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 mb-4">
      <div className="flex flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wider text-white/30 font-medium">From</label>
        <input
          type="date"
          value={filters.from}
          onChange={(e) => set("from", e.target.value)}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 focus:border-white/20 focus:outline-none focus:bg-white/10 transition [color-scheme:dark]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wider text-white/30 font-medium">To</label>
        <input
          type="date"
          value={filters.to}
          onChange={(e) => set("to", e.target.value)}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 focus:border-white/20 focus:outline-none focus:bg-white/10 transition [color-scheme:dark]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Customer</label>
        <input
          type="text"
          placeholder="Name or email…"
          value={filters.customer}
          onChange={(e) => set("customer", e.target.value)}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 placeholder-white/20 focus:border-white/20 focus:outline-none focus:bg-white/10 transition min-w-[160px]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Company</label>
        <input
          type="text"
          placeholder="Company…"
          value={filters.company}
          onChange={(e) => set("company", e.target.value)}
          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 placeholder-white/20 focus:border-white/20 focus:outline-none focus:bg-white/10 transition min-w-[140px]"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[10px] uppercase tracking-wider text-white/30 font-medium">Agents</label>
        <AgentMultiSelect
          allAgents={allAgents}
          selected={filters.agents}
          onChange={(v) => set("agents", v)}
        />
      </div>
      {activeCount > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1.5 self-end rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/50 hover:text-white/80 hover:bg-white/10 transition"
        >
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-cyan-500/30 text-cyan-300 text-[10px] font-bold">
            {activeCount}
          </span>
          Clear all
        </button>
      )}
    </div>
  );
}

export default function OrdersTab() {
  const { adminFetch } = useAdminAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [allAgents, setAllAgents] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const activeCount = countActiveFilters(filters);

  const load = useCallback(
    async (f: Filters) => {
      setLoading(true);
      setError("");
      try {
        const res = await adminFetch(buildQuery(f));
        if (!res.ok) throw new Error("Failed to load orders");
        setOrders(await res.json());
      } catch (e: unknown) {
        setError(errMsg(e));
      } finally {
        setLoading(false);
      }
    },
    [adminFetch],
  );

  useEffect(() => { load(filters); }, [load, filters]);

  useEffect(() => {
    adminFetch("api/admin/agents-list")
      .then((r) => (r.ok ? r.json() : []))
      .then((names: string[]) => setAllAgents(names))
      .catch(() => {});
  }, [adminFetch]);

  const copyPaymentId = async (orderId: number, paymentId: string) => {
    try {
      await navigator.clipboard.writeText(paymentId);
      setCopiedId(orderId);
      setTimeout(() => setCopiedId((cur) => (cur === orderId ? null : cur)), 1500);
    } catch {
    }
  };

  return (
    <div>
      <FilterBar
        filters={filters}
        allAgents={allAgents}
        activeCount={activeCount}
        onChange={setFilters}
        onClear={() => setFilters(EMPTY_FILTERS)}
      />

      {loading ? (
        <div className="text-white/50 text-sm">Loading orders…</div>
      ) : error ? (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">
          {error}
        </div>
      ) : orders.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-12 text-center">
          {activeCount > 0 ? (
            <>
              <div className="text-white/40 text-sm mb-2">No orders match your filters</div>
              <button
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-xs text-cyan-400/70 hover:text-cyan-300 transition"
              >
                Clear filters
              </button>
            </>
          ) : (
            <>
              <div className="text-white/40 text-sm mb-2">No orders yet</div>
              <div className="text-white/30 text-xs">
                Completed checkouts will appear here automatically.
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-white/10 bg-white/5">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-white/5 text-white/50 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Company</th>
                  <th className="px-4 py-3 font-medium">Agents</th>
                  <th className="px-4 py-3 font-medium text-right">Total</th>
                  <th className="px-4 py-3 font-medium text-right">Deposit</th>
                  <th className="px-4 py-3 font-medium">Coupon</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Stripe ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-white/[0.02] transition">
                    <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-white">{order.customerName}</div>
                      <div className="text-white/40 text-xs">{order.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white/60">
                      {order.company ?? <span className="text-white/30">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <ul className="space-y-1">
                        {order.agents.map((a, i) => (
                          <li key={i} className="text-white/80 text-xs">
                            <span className="text-white">{a.name}</span>
                            <span className="text-white/40"> · {formatMoney(a.price)}</span>
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-3 text-right text-white whitespace-nowrap">
                      {formatMoney(order.totalPrice)}
                    </td>
                    <td className="px-4 py-3 text-right text-white/70 whitespace-nowrap">
                      {formatMoney(order.depositAmount)}
                    </td>
                    <td className="px-4 py-3">
                      {order.couponCode ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-mono text-xs font-bold text-cyan-300">{order.couponCode}</span>
                          {order.discountAmount != null && (
                            <span className="text-xs text-white/50">−{formatMoney(order.discountAmount)}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                          order.status === "paid"
                            ? "bg-green-500/20 text-green-300"
                            : "bg-white/10 text-white/60"
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {order.stripePaymentIntentId ? (
                        <button
                          onClick={() =>
                            copyPaymentId(order.id, order.stripePaymentIntentId!)
                          }
                          title="Click to copy"
                          className="font-mono text-xs text-white/50 hover:text-white/90 transition"
                        >
                          {copiedId === order.id
                            ? "Copied!"
                            : `${order.stripePaymentIntentId.slice(0, 14)}…`}
                        </button>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
